import { config } from "../Environment";

// ── Types ──────────────────────────────────────────────────────────────────

export interface ExpectedOutput {
  id: string;
  label: string;
  type: "activity" | "identity_space" | "program" | "external_link" | "text";
}

export interface SubmittedOutput {
  expectedOutputId: string;
  value: string | null;
  linkedEntityType?: string | null;
  linkedEntityName?: string | null;
  submittedAt?: number | null;
}

export interface Milestone {
  id: string;
  title: string;
  type: "activity" | "meeting" | "action_item" | "identity_space" | "instruction";
  linkedEntityId?: string | null;
  linkedEntityName?: string | null;
  linkedProcessId?: string | null;
  order: number;
  dueDate?: number | null;
  dueDateOffset?: number | null;
  status: "pending" | "in_progress" | "completed";
  isOptional: boolean;
  parallelWithPrevious?: boolean;
  notes?: string | null;
  completedAt?: number | null;
  expectedOutputs?: ExpectedOutput[];
  submittedOutputs?: SubmittedOutput[];
}

export interface Program {
  _id: string;
  title: string;
  description: string;
  fiMentor: { _id: string; name: string; email: string } | string;
  fiClient?: { _id: string; name: string; email: string } | string | null;
  status: "draft" | "active" | "paused" | "completed";
  milestones: Milestone[];
  isTemplate?: boolean;
  isOnboarding?: boolean;
  sourceTemplateId?: string | null;
  sourceTemplateName?: string | null;
  stalledThresholdDays?: number | null;
  /** Automatic AI-credit charge-up when this program is assigned (once per user) */
  creditGrant?: { enabled: boolean; amount: number };
  assignedAt?: string | null;
  createdOn: string;
  lastUpdated: string;
  // PWF Hybrid Bridge — present only on items sourced from the pwfs collection
  __isPwf?: boolean;
  pwfMeta?: {
    fiParent: string | null;
    ancestorIds: string[];
    children: string[];
    aiContext: any | null;
  };
  manifestations?: Array<{ pwfType: string; isActive: boolean; enabledAt: string; config: Record<string, any> }>;
  config?: Record<string, any>;
}

export interface CreateProgramRequest {
  title: string;
  description?: string;
  isTemplate?: boolean;
}

export interface UpdateProgramRequest {
  title?: string;
  description?: string;
  status?: "draft" | "active" | "paused" | "completed";
  stalledThresholdDays?: number | null;
  isTemplate?: boolean;
  isOnboarding?: boolean;
  creditGrant?: { enabled: boolean; amount: number };
}

export interface AddMilestoneRequest {
  title: string;
  type: "activity" | "meeting" | "action_item" | "identity_space" | "instruction";
  linkedEntityId?: string;
  linkedEntityName?: string;
  isOptional?: boolean;
  parallelWithPrevious?: boolean;
  notes?: string;
  dueDate?: number | null;
  dueDateOffset?: number | null;
  expectedOutputs?: Array<{ id?: string; label: string; type: ExpectedOutput["type"] }>;
}

export interface UpdateMilestoneRequest {
  title?: string;
  type?: "activity" | "meeting" | "action_item" | "identity_space" | "instruction";
  linkedEntityId?: string | null;
  linkedEntityName?: string | null;
  isOptional?: boolean;
  parallelWithPrevious?: boolean;
  notes?: string | null;
  dueDate?: number | null;
  dueDateOffset?: number | null;
  expectedOutputs?: Array<{ id?: string; label: string; type: ExpectedOutput["type"] }>;
}

// ── API functions ──────────────────────────────────────────────────────────

export const createProgram = async (
  retryFetch: Function,
  data: CreateProgramRequest
): Promise<{ program: Program }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create program: ${res.statusText}`);
  return res.json();
};

export const listPrograms = async (
  retryFetch: Function,
  params?: { status?: string }
): Promise<{ programs: Program[] }> => {
  const qs = params?.status ? `?status=${params.status}` : "";
  const res = await retryFetch(`${config.API_BASE}/api/programs${qs}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list programs: ${res.statusText}`);
  return res.json();
};

export const listAssignedPrograms = async (
  retryFetch: Function
): Promise<{ programs: Program[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/assigned`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list assigned programs: ${res.statusText}`);
  return res.json();
};

export const getProgram = async (
  retryFetch: Function,
  id: string
): Promise<{ program: Program }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${id}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to get program: ${res.statusText}`);
  return res.json();
};

export const updateProgram = async (
  retryFetch: Function,
  id: string,
  data: UpdateProgramRequest
): Promise<{ program: Program }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to update program: ${res.statusText}`);
  return res.json();
};

export const addMilestone = async (
  retryFetch: Function,
  programId: string,
  milestone: AddMilestoneRequest
): Promise<{ milestone: Milestone }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${programId}/milestones`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(milestone),
  });
  if (!res.ok) throw new Error(`Failed to add milestone: ${res.statusText}`);
  return res.json();
};

export const updateMilestone = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string,
  data: UpdateMilestoneRequest
): Promise<{ milestone: Milestone }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );
  if (!res.ok) throw new Error(`Failed to update milestone: ${res.statusText}`);
  return res.json();
};

export const removeMilestone = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string
): Promise<{ milestones: Milestone[] }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}`,
    {
      method: "DELETE",
      credentials: "include",
    }
  );
  if (!res.ok) throw new Error(`Failed to remove milestone: ${res.statusText}`);
  return res.json();
};

export const reorderMilestones = async (
  retryFetch: Function,
  programId: string,
  orderedIds: string[]
): Promise<{ milestones: Milestone[] }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/reorder`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ orderedIds }),
    }
  );
  if (!res.ok) throw new Error(`Failed to reorder milestones: ${res.statusText}`);
  return res.json();
};

export const unassignProgram = async (
  retryFetch: Function,
  programId: string
): Promise<{ program: Program }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${programId}/unassign`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to unassign program: ${res.statusText}`);
  return res.json();
};

export const assignProgram = async (
  retryFetch: Function,
  programId: string,
  clientId: string
): Promise<{ program: Program; spaceCreationWarnings?: string[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${programId}/assign`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ clientId }),
  });
  if (!res.ok) throw new Error(`Failed to assign program: ${res.statusText}`);
  return res.json();
};

export const completeMilestone = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string
): Promise<{ milestone: Milestone }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/complete`,
    {
      method: "PATCH",
      credentials: "include",
    }
  );
  if (!res.ok) throw new Error(`Failed to complete milestone: ${res.statusText}`);
  return res.json();
};

export const clientCompleteMilestone = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string
): Promise<{ milestone: Milestone }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/client-complete`,
    {
      method: "PATCH",
      credentials: "include",
    }
  );
  if (!res.ok) throw new Error(`Failed to complete milestone: ${res.statusText}`);
  return res.json();
};

export interface OutputTakeaway {
  _id: string;
  type: "action" | "insight";
  content: string;
  status: "pending" | "completed" | "archived";
  completedOn?: number | null;
  createdOn: number;
}

export interface OutputMeeting {
  _id: string;
  name?: string;
  aiSummary?: string | null;
  aiSummaryGeneratedAt?: number | null;
  endedAt?: number | null;
}

export interface OutputEngagement {
  _id: string;
  status: "started" | "in_progress" | "completed" | "terminated";
  totalActivitiesCompleted: number;
  totalActivitiesReached: number;
  totalTimeSpentMs: number;
  startedAt: number;
  completedAt?: number | null;
  lastActivityAt?: number | null;
}

export interface OutputEngagementRun {
  processId: string;
  name?: string | null;
  status: string;
  started: number;
  lastUpdated?: number | null;
}

export interface OutputExample {
  _id: string;
  exampleName?: string;
  exampleDescription?: string;
  prescriptionName?: string;
  isPublished?: boolean;
  publishedOn?: number | null;
  started: number;
}

export interface OutputIdentitySpace {
  _id: string;
  name: string;
  color: string;
  icon: string;
  status: string;
  identityLabel?: string;
  vision?: string;
}

export interface MilestoneOutputs {
  takeaways: OutputTakeaway[];
  meeting: OutputMeeting | null;
  takeaway: OutputTakeaway | null;
  engagement?: OutputEngagement | null;
  examples?: OutputExample[];
  processId?: string | null;
  engagementRuns?: OutputEngagementRun[];
  identitySpace?: OutputIdentitySpace | null;
  expectedOutputs: ExpectedOutput[];
  submittedOutputs: SubmittedOutput[];
}

export const getMilestoneOutputs = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string
): Promise<MilestoneOutputs> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/outputs`,
    { method: "GET", credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to fetch milestone outputs: ${res.statusText}`);
  return res.json();
};

export const listTemplates = async (
  retryFetch: Function
): Promise<{ templates: Program[] }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/templates`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to list templates: ${res.statusText}`);
  return res.json();
};

export const copyTemplate = async (
  retryFetch: Function,
  templateId: string
): Promise<{ program: Program }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${templateId}/copy`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to copy template: ${res.statusText}`);
  return res.json();
};

export const deleteProgram = async (
  retryFetch: Function,
  programId: string
): Promise<{ success: boolean }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/${programId}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to delete program: ${res.statusText}`);
  return res.json();
};

export const submitMilestoneOutput = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string,
  data: {
    expectedOutputId: string;
    value: string;
    linkedEntityType?: string;
    linkedEntityName?: string;
  }
): Promise<{ submittedOutputs: SubmittedOutput[] }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/outputs`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );
  if (!res.ok) throw new Error(`Failed to submit milestone output: ${res.statusText}`);
  return res.json();
};

export const listClientSpaces = async (
  retryFetch: Function,
  programId: string
): Promise<{ spaces: OutputIdentitySpace[] }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/client-spaces`,
    { credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to fetch client spaces: ${res.statusText}`);
  return res.json();
};

export const getOnboardingProgram = async (
  retryFetch: Function
): Promise<{ program: Program | null }> => {
  const res = await retryFetch(`${config.API_BASE}/api/programs/onboarding`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Failed to get onboarding program: ${res.statusText}`);
  return res.json();
};

/** Admin: list all onboarding programs across all users */
export const listAllOnboardingPrograms = async (
  retryFetch: Function
): Promise<{ programs: Program[] }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/sysadmin/programs/onboarding`,
    { credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to list onboarding programs: ${res.statusText}`);
  return res.json();
};

/** Client: create an identity space for a milestone that has no linked space yet */
export const createMilestoneSpace = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string,
  data: { name: string; color?: string; icon?: string }
): Promise<{ milestone: Milestone; space: OutputIdentitySpace }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/create-space`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(data),
    }
  );
  if (!res.ok) throw new Error(`Failed to create space: ${res.statusText}`);
  return res.json();
};

/** Client: opt into an optional pending milestone (and auto-create its identity space if applicable) */
export const activateOptionalMilestone = async (
  retryFetch: Function,
  programId: string,
  milestoneId: string
): Promise<{ milestone: Milestone }> => {
  const res = await retryFetch(
    `${config.API_BASE}/api/programs/${programId}/milestones/${milestoneId}/activate-optional`,
    { method: "POST", credentials: "include" }
  );
  if (!res.ok) throw new Error(`Failed to activate optional milestone: ${res.statusText}`);
  return res.json();
};
